import {
  cloneElement,
  isValidElement,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import './tooltip.css';

interface TooltipProps {
  /** Bold one-word/short heading, e.g. "Circle". */
  heading: string;
  /** One-line description, e.g. "Measure or annotate a circle and its area." */
  description: string;
  /** A single focusable/hoverable element (button, etc). No native `title` fallback. */
  children: ReactElement;
  /** Hover delay before the card appears, ms. */
  delay?: number;
}

/**
 * Hover card: a small floating panel with a bold heading and a
 * one-line description, replacing plain browser `title` tooltips across the
 * app's icon-only controls. Rendered via a portal to `document.body` so it
 * is never clipped by an ancestor's `overflow` (the toolbars it's used in
 * are exactly that kind of container).
 */
export function Tooltip({ heading, description, children, delay = 200 }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const timerRef = useRef<number | null>(null);

  function clearTimer() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }

  function show(target: HTMLElement) {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      const rect = target.getBoundingClientRect();
      setCoords({ x: rect.left + rect.width / 2, y: rect.top });
      setVisible(true);
    }, delay);
  }

  function hide() {
    clearTimer();
    setVisible(false);
  }

  if (!isValidElement(children)) return children;

  const child = children as ReactElement<Record<string, unknown>>;
  const triggerProps = {
    onMouseEnter: (e: MouseEvent) => show(e.currentTarget as HTMLElement),
    onMouseLeave: hide,
    onFocus: (e: FocusEvent) => show(e.currentTarget as HTMLElement),
    onBlur: hide,
  };

  return (
    <>
      {cloneElement(child, triggerProps)}
      {visible &&
        coords &&
        createPortal(
          <div
            className="tooltip-card"
            role="tooltip"
            style={{ left: coords.x, top: coords.y - 8 }}
          >
            <div className="tooltip-heading">{heading}</div>
            <div className="tooltip-description">{description}</div>
          </div>,
          document.body,
        )}
    </>
  );
}
